"use client";

import * as React from "react";

import { cn } from "@immi/ui/lib/utils";
import { Label } from "@immi/ui/components/label";

type FieldProps = Omit<React.ComponentProps<"div">, "children"> & {
  label?: React.ReactNode;
  hint?: React.ReactNode;
  error?: React.ReactNode;
  required?: boolean;
  htmlFor?: string;
  children: React.ReactNode;
};

type ControlProps = {
  id?: string;
  "aria-describedby"?: string;
  "aria-invalid"?: boolean | "true" | "false";
};

function Field({
  className,
  label,
  hint,
  error,
  required = false,
  htmlFor,
  children,
  ...props
}: FieldProps) {
  const generatedId = React.useId();
  const controlId = htmlFor ?? generatedId;
  const messageId = `${controlId}-message`;
  const message = error ?? hint;

  const control = React.isValidElement<ControlProps>(children)
    ? React.cloneElement(children, {
        id: children.props.id ?? controlId,
        "aria-describedby":
          children.props["aria-describedby"] ??
          (message ? messageId : undefined),
        "aria-invalid":
          children.props["aria-invalid"] ?? (error ? true : undefined),
      })
    : children;

  return (
    <div
      data-slot="field"
      data-invalid={error ? "true" : undefined}
      className={cn("flex flex-col gap-1.5", className)}
      {...props}
    >
      {label && (
        <Label htmlFor={controlId} className="text-xs text-ink-muted">
          {label}
          {required && (
            <span aria-hidden className="text-feedback-error-accent">
              *
            </span>
          )}
        </Label>
      )}
      {control}
      {message && (
        <p
          id={messageId}
          className={cn(
            "text-[11px] leading-snug",
            error ? "text-feedback-error-text" : "text-ink-subtle",
          )}
        >
          {message}
        </p>
      )}
    </div>
  );
}

export { Field };
export type { FieldProps };
